import { failingFetcher } from '@lib/http/fetch';
import * as Sentry from '@sentry/nextjs';
import {
  CoveoSearchHub,
  SEARCH_TOKEN_SHAPE_PARAM,
  SEARCH_TOKEN_SHAPE_TYPED,
} from './coveoConstants';

const SEARCH_TOKEN_LOCAL_STORAGE_KEY = (searchHub: CoveoSearchHub) =>
  `coveoSearchToken-${searchHub}`;

export type TokenStep = 'token_fetch' | 'token_renew';

export type SearchTokenResponse = { token: string; userGroups: string[] };

/**
 * Fetches a fresh Coveo search token + userGroups from the server, without
 * touching the localStorage cache. Throws (via failingFetcher) on a non-2xx
 * response; returns null when a 2xx body lacks a valid token.
 *
 * `anonymous` is required rather than defaulted: an omitted argument would
 * silently drop internal-user boosting, which the route bakes into the token
 * only when it reads the session cookie (`anonymous !== 'true'`). The SSR
 * search path serves all-public content and opts in; the CSR/legacy engines
 * pass `false` to keep their PER-USER token.
 */
export const fetchSearchToken = async (
  searchHub: CoveoSearchHub,
  anonymous: boolean
): Promise<SearchTokenResponse | null> => {
  const anonymousParam = anonymous ? '&anonymous=true' : '';
  const shapeParam = `&${SEARCH_TOKEN_SHAPE_PARAM}=${SEARCH_TOKEN_SHAPE_TYPED}`;
  const result = await failingFetcher(
    `/api/coveo/search-token?searchHub=${searchHub}${anonymousParam}${shapeParam}`,
    { cache: 'no-store' }
  );

  // A pod still running the previous release ignores the unknown shape param
  // and answers with a bare token string. Both shapes have to be accepted until
  // old pods have drained, or search breaks for the duration of every rollout
  // and rollback.
  if (typeof result === 'string' && result) {
    return { token: result, userGroups: [] };
  }

  if (result && typeof result.token === 'string' && result.token) {
    return {
      token: result.token,
      userGroups: Array.isArray(result.userGroups) ? result.userGroups : [],
    };
  }

  return null;
};

/**
 * Calls the server to request a new Coveo search token, and saves the new token to localStorage.
 *
 * When `rethrowOnError` is true, the function skips its internal Sentry capture and rethrows the
 * underlying error so the caller can attach its own scope/tags before reporting. Used by
 * `createEngineModule.doInit` so the boundary owns a single, fully-tagged Sentry event per failure.
 *
 * `step` controls the Sentry tag for the helper's own captures: pass `'token_renew'` when invoked
 * from a Coveo SDK `renewAccessToken` callback so renewal failures don't masquerade as init failures.
 */
export const getNewSearchToken = async (
  searchHub: CoveoSearchHub,
  {
    rethrowOnError = false,
    step = 'token_fetch',
  }: { rethrowOnError?: boolean; step?: TokenStep } = {}
): Promise<string | null> => {
  try {
    // anonymous=false → per-user token (internal userGroups).
    const result = await fetchSearchToken(searchHub, false);

    if (result?.token) {
      setSavedSearchToken(searchHub, JSON.stringify(result.token));
      return result.token;
    }

    const invalidResponseError = new Error(
      'Error in getNewSearchToken: invalid response (missing token)'
    );
    if (rethrowOnError) throw invalidResponseError;

    Sentry.withScope((scope) => {
      scope.setTags({
        'coveo.hub': searchHub,
        'coveo.step': step,
      });
      scope.setContext('coveo_init', {
        hub: searchHub,
        reason: 'invalid_response',
      });
      Sentry.captureException(invalidResponseError);
    });
  } catch (error) {
    if (rethrowOnError) throw error;

    Sentry.withScope((scope) => {
      scope.setTags({
        'coveo.hub': searchHub,
        'coveo.step': step,
      });
      scope.setContext('coveo_init', {
        hub: searchHub,
        reason: 'fetch_threw',
      });
      Sentry.captureException(error);
    });
  }

  return null;
};

/** Grabs the coveo search token from localStorage */
export const getSavedSearchToken = (
  searchHub: CoveoSearchHub
): string | null => {
  return localStorage?.getItem(SEARCH_TOKEN_LOCAL_STORAGE_KEY(searchHub));
};

/** Saves the coveo search token to localStorage */
export const setSavedSearchToken = (
  searchHub: CoveoSearchHub,
  newToken: string
): void => {
  localStorage?.setItem(SEARCH_TOKEN_LOCAL_STORAGE_KEY(searchHub), newToken);
};

/** Removes the coveo search token from localStorage */
export const resetSearchTokens = () => {
  localStorage?.removeItem(
    SEARCH_TOKEN_LOCAL_STORAGE_KEY(CoveoSearchHub.SEARCH)
  );
  localStorage?.removeItem(
    SEARCH_TOKEN_LOCAL_STORAGE_KEY(CoveoSearchHub.LISTING)
  );
};

export const getSearchTokenGroups = (searchToken: string | null) => {
  if (!searchToken) {
    return;
  }
  const base64Url = searchToken.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const parsedToken = JSON.parse(window.atob(base64));

  return parsedToken?.userGroups as Array<string>;
};
